import { getTilesetDef, resolvePresetCells } from './Tilesets'

const BITS = { N: 1, E: 2, S: 4, W: 8, NW: 16, NE: 32, SE: 64, SW: 128 }

// Determines which half-cell within a 2×3 block to sample for a given quarter
// and neighbor bitmask.
export function pickQuarterShape(kind, bits) {
  if (kind === 'tl') {
    const n = !!(bits & BITS.N), w = !!(bits & BITS.W), nw = !!(bits & BITS.NW)
    if (n && w) return nw ? { row: 4, col: 2 } : { row: 0, col: 2 }
    if (n) return { row: 4, col: 0 }
    if (w) return { row: 2, col: 2 }
    return { row: 2, col: 0 }
  }
  if (kind === 'tr') {
    const n = !!(bits & BITS.N), e = !!(bits & BITS.E), ne = !!(bits & BITS.NE)
    if (n && e) return ne ? { row: 4, col: 1 } : { row: 0, col: 3 }
    if (n) return { row: 4, col: 3 }
    if (e) return { row: 2, col: 1 }
    return { row: 2, col: 3 }
  }
  if (kind === 'bl') {
    const s = !!(bits & BITS.S), w = !!(bits & BITS.W), sw = !!(bits & BITS.SW)
    if (s && w) return sw ? { row: 3, col: 2 } : { row: 1, col: 2 }
    if (s) return { row: 3, col: 0 }
    if (w) return { row: 5, col: 2 }
    return { row: 5, col: 0 }
  }
  // br
  const s = !!(bits & BITS.S), e = !!(bits & BITS.E), se = !!(bits & BITS.SE)
  if (s && e) return se ? { row: 3, col: 1 } : { row: 1, col: 3 }
  if (s) return { row: 3, col: 3 }
  if (e) return { row: 5, col: 1 }
  return { row: 5, col: 3 }
}

// ── Neighbour matching ──────────────────────────────────────────────────────

function isSamePreset(entry, tilesetUrl, baseCx, baseCy) {
  if (!entry || typeof entry !== 'object') return false
  if (entry.ts !== tilesetUrl) return false
  if (entry.x != null) return entry.x === baseCx && entry.y === baseCy
  return false
}

// ── State calculation ───────────────────────────────────────────────────────

export function calcTilesetAutotileDesc(tilesetId, presetId, x, y, getEntryAtWorld) {
  const preset = getTilesetDef(tilesetId)?.presets?.[presetId]
  if (!preset) return null

  const tilesetUrl = getTilesetDef(tilesetId).url
  const allBaseCells = resolvePresetCells(preset)
  const [baseCx, baseCy] = allBaseCells[0] ?? [0, 0]

  const same = (ox, oy) => isSamePreset(getEntryAtWorld(x + ox, y + oy), tilesetUrl, baseCx, baseCy)

  let bits = 0
  if (same(0, 1))  bits |= BITS.N
  if (same(1, 0))  bits |= BITS.E
  if (same(0, -1)) bits |= BITS.S
  if (same(-1, 0)) bits |= BITS.W
  // Diagonal bits only matter when both adjacent cardinals are set — keeps variants to 47 max.
  if ((bits & (BITS.N | BITS.W)) === (BITS.N | BITS.W) && same(-1,  1)) bits |= BITS.NW
  if ((bits & (BITS.N | BITS.E)) === (BITS.N | BITS.E) && same( 1,  1)) bits |= BITS.NE
  if ((bits & (BITS.S | BITS.E)) === (BITS.S | BITS.E) && same( 1, -1)) bits |= BITS.SE
  if ((bits & (BITS.S | BITS.W)) === (BITS.S | BITS.W) && same(-1, -1)) bits |= BITS.SW

  const desc = { ts: tilesetUrl, x: baseCx, y: baseCy, layer: preset.layer, bits }
  if (preset.collider) desc.collider = preset.collider
  if (preset.fps) desc.fps = preset.fps
  if (allBaseCells.length > 1) desc.frames = allBaseCells.slice(1)
  return desc
}

// Paint an autotile preset at (x, y) and update the 8 surrounding same-preset
// cells. Returns an array of { x, y, tile } change objects.
export function applyTilesetAutotileAtWorld(tilesetId, presetId, x, y, { getEntryAtWorld, setTileDescAtWorld }) {
  const preset = getTilesetDef(tilesetId)?.presets?.[presetId]
  if (!preset) return []

  const tilesetUrl = getTilesetDef(tilesetId).url
  const [baseCx, baseCy] = resolvePresetCells(preset)[0] ?? [0, 0]
  const changes = []

  const desc = calcTilesetAutotileDesc(tilesetId, presetId, x, y, getEntryAtWorld)
  if (desc && setTileDescAtWorld(x, y, desc)) changes.push({ x, y, tile: desc })

  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue
      const cx = x + dx, cy = y + dy
      const neighbor = getEntryAtWorld(cx, cy)
      if (!isSamePreset(neighbor, tilesetUrl, baseCx, baseCy)) continue
      const newDesc = calcTilesetAutotileDesc(tilesetId, presetId, cx, cy, getEntryAtWorld)
      if (newDesc && setTileDescAtWorld(cx, cy, newDesc)) changes.push({ x: cx, y: cy, tile: newDesc })
    }
  }

  return changes
}

// ── Atlas key helpers ───────────────────────────────────────────────────────

// Descriptor shape:
//   x, y   — top-left cell of the 2×3 autotile block (frame 0)
//   frames — additional top-left cells for animated presets [[cx,cy],...]
//   fps    — animation speed (animated only)
//   bits   — normalized 8-direction neighbor bitmask (max 47 distinct values)
export function tilesetAutotileKey(tilesetId, x, y, bits, desc) {
  const allBase = [[x, y], ...(desc.frames || [])]
  const basePart = allBase.map(f => f.join(',')).join(';')
  let key = `ts-at:${tilesetId}@${basePart}[bits=${bits}`
  if (desc.fps) key += `,fps=${desc.fps}`
  return key + ']'
}

export function parseTilesetAutotileKey(key) {
  if (!key?.startsWith('ts-at:')) return null
  const rest = key.slice(6)
  const at = rest.indexOf('@')
  if (at === -1) return null
  const tilesetId = rest.slice(0, at)
  const bracketStart = rest.indexOf('[', at)
  const bracketEnd = rest.lastIndexOf(']')
  const cellPart = rest.slice(at + 1, bracketStart === -1 ? undefined : bracketStart)
  const allBaseCells = cellPart.split(';').map(seg => seg.split(',').map(Number))
  const [x, y] = allBaseCells[0]
  const frames = allBaseCells.slice(1)
  let bits = 0, fps = 0
  if (bracketStart !== -1 && bracketEnd !== -1) {
    for (const param of rest.slice(bracketStart + 1, bracketEnd).split(',')) {
      const eq = param.indexOf('=')
      if (eq === -1) continue
      const k = param.slice(0, eq), v = param.slice(eq + 1)
      if (k === 'bits') bits = Number(v)
      else if (k === 'fps') fps = Number(v)
    }
  }
  return { tilesetId, x, y, frames, fps, bits }
}
